import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { G, Path, Rect, Svg } from '../../svg';
import { useTheme } from '../../theme';

/**
 * What a note and a mind map look like BEFORE anything is in them — Figma
 * `44:618` and `44:626` (Multi-Source-Set `44:596`).
 *
 * A deck's note and its map exist from the moment they are made, so their
 * tiles are on the grid before there is a word or a node to draw. Until this
 * they showed the bare framed card, which reads as a broken cover rather than
 * an empty one. Each now shows the SHAPE of the thing it will become — lines
 * of writing, a branching tree — greyed out, so the tile says "nothing here
 * yet" in the form of the thing itself. The caption underneath still says
 * "Not generated"; this is the picture that goes with it.
 *
 * Drawn as SVG in the TILE's own coordinate space (the frame's 219.5×309.354,
 * which is the 171:241 tile ratio), so every bar keeps the frame's proportions
 * at any tile width and both platforms draw one set of primitives. The
 * `MindmapMiniature` beside these does the same for a map that HAS nodes.
 */

/** The frame these are drawn against — the Figma tile, at its own scale. */
const FRAME_WIDTH = 219.5;
const FRAME_HEIGHT = 309.354;
const VIEW_BOX = `0 0 ${FRAME_WIDTH} ${FRAME_HEIGHT}`;

/** Figma `55:25784`…`55:25800`: 16 lines of writing, 10 tall on a 14 pitch. */
const NOTE_LINE_WIDTHS = [
  171, 156, 134, 152, 163, 152, 171, 83, 156, 126, 146, 156, 156, 146, 108, 146,
];
const NOTE_LINE_LEFT = 24;
const NOTE_LINE_TOP = 62;
const NOTE_LINE_HEIGHT = 10;
const NOTE_LINE_PITCH = 14;

export function EmptyNoteMiniature() {
  const { isDark } = useTheme();
  // The miniature sits on a tile that, in dark, takes no fill of its own (the
  // pane's surface — elevation.ts). `secondary-200` is the tile's hairline
  // there, so the ink steps one further to read as lines, not as the rim.
  const fill = brandColor('secondary', isDark ? '300' : '200', isDark);

  return (
    <View className="absolute inset-0 overflow-hidden">
      <Svg width="100%" height="100%" viewBox={VIEW_BOX}>
        {NOTE_LINE_WIDTHS.map((width, index) => (
          <Rect
            key={index}
            x={NOTE_LINE_LEFT}
            y={NOTE_LINE_TOP + index * NOTE_LINE_PITCH}
            width={width}
            height={NOTE_LINE_HEIGHT}
            rx={2}
            fill={fill}
          />
        ))}
      </Svg>
    </View>
  );
}
(EmptyNoteMiniature as { displayName?: string }).displayName = 'EmptyNoteMiniature';

/**
 * Figma `55:25825`: a five-row tree — four leaves, a pair, the root, a pair,
 * four leaves — with the branch lines running behind the nodes.
 */
const MINDMAP_NODE_HEIGHT = 15.75;
const MINDMAP_NODE_RADIUS = 3.5;
/** The leaf rows: four 28-wide nodes on a 42 pitch. */
const MINDMAP_LEAF_XS = [32.5, 74.5, 116.5, 158.5];
const MINDMAP_LEAF_WIDTH = 28;
const MINDMAP_LEAF_YS = [84, 231];
/** The branch rows: two 49.875-wide nodes either side of the centre line. */
const MINDMAP_BRANCH_XS = [52.625, 116.5];
const MINDMAP_BRANCH_WIDTH = 49.875;
const MINDMAP_BRANCH_YS = [120.75, 194.25];
/** The root, centred. */
const MINDMAP_ROOT_X = 85;
const MINDMAP_ROOT_Y = 157.5;

/**
 * ONE branch bundle — root out to a pair and on to two leaves — exported by
 * Figma as `Vector 3`, in its own 64.3125×57.75 box. The frame places it four
 * times, mirrored into each quadrant around the root, which is what the
 * transforms below are: the tree is one shape drawn four ways, not four
 * shapes. Transcribed from the exported vector, not redrawn.
 */
const MINDMAP_BRANCH_PATH =
  'M63.875 0V4.8125C63.875 6.7455 62.308 8.3125 60.375 8.3125H37.625C35.692 8.3125 ' +
  '34.125 9.8795 34.125 11.8125V14.875V45.0625M34.125 45.0625H3.9375C2.0045 45.0625 ' +
  '0.4375 46.6295 0.4375 48.5625V57.75M34.125 45.0625H40.6875C42.6205 45.0625 ' +
  '44.1875 46.6295 44.1875 48.5625V57.75';
const MINDMAP_BRANCH_STROKE = 0.875;
/**
 * Where each copy lands. The exported box carries the stroke's overflow, so
 * its origin sits 0.4375 left of the drawn edge; a mirrored copy is placed by
 * its far edge instead (`scale(-1 …)` counts x backwards from the translate).
 */
const MINDMAP_BRANCH_TRANSFORMS = [
  'translate(45.1875 173.25)',
  'translate(45.1875 157.5) scale(1 -1)',
  'translate(173.375 173.25) scale(-1 1)',
  'translate(173.375 157.5) scale(-1 -1)',
];

export function EmptyMindmapMiniature() {
  const { isDark } = useTheme();
  // One step past the nested tile in dark — see EmptyNoteMiniature.
  const ink = brandColor('secondary', isDark ? '300' : '200', isDark);
  const node = (x: number, y: number, width: number, key: string) => (
    <Rect
      key={key}
      x={x}
      y={y}
      width={width}
      height={MINDMAP_NODE_HEIGHT}
      rx={MINDMAP_NODE_RADIUS}
      fill={ink}
    />
  );

  return (
    <View className="absolute inset-0 overflow-hidden">
      <Svg width="100%" height="100%" viewBox={VIEW_BOX}>
        {/* The branches first: the nodes are painted over them, which is what
            makes a line read as running behind a node rather than into it. */}
        {MINDMAP_BRANCH_TRANSFORMS.map((transform, index) => (
          <G key={index} transform={transform}>
            <Path
              d={MINDMAP_BRANCH_PATH}
              stroke={ink}
              strokeWidth={MINDMAP_BRANCH_STROKE}
              fill="none"
            />
          </G>
        ))}
        {MINDMAP_LEAF_YS.map((y) =>
          MINDMAP_LEAF_XS.map((x) => node(x, y, MINDMAP_LEAF_WIDTH, `leaf-${x}-${y}`)),
        )}
        {MINDMAP_BRANCH_YS.map((y) =>
          MINDMAP_BRANCH_XS.map((x) =>
            node(x, y, MINDMAP_BRANCH_WIDTH, `branch-${x}-${y}`),
          ),
        )}
        {node(MINDMAP_ROOT_X, MINDMAP_ROOT_Y, MINDMAP_BRANCH_WIDTH, 'root')}
      </Svg>
    </View>
  );
}
(EmptyMindmapMiniature as { displayName?: string }).displayName = 'EmptyMindmapMiniature';
